import { computed, DestroyRef, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Viewer3dInitOptions, Viewer3dService as Viewer3dSdkService } from '@3ddv/ngx-dvm-internal';
import { Viewer3dLoadView3dOptions, Viewer3dFlagsAPI, Viewer3dPlugins, Viewer3dLoadObject } from '@3ddv/dvm-internal';
import { Configuration } from '@models/interfaces/configuration/index.interface';
import { APP_CONFIG } from '@configuration/configuration';
import { catchError, first, from, Observable, of, switchMap, tap } from 'rxjs';
import { VIEWER3D } from '@dvm.providers';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { UiService } from '@services/ui.service';
import { ToastService } from '@services/toast.service';
import { ViewDeterminationService } from './view-determination.service';
import { VenueConfigurationsService } from './venue-configurations.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


@Injectable({
  providedIn: 'root'
})
export class Viewer3dService {

  // CONFIGURATION
  private readonly config:                Signal<Configuration>            = inject(APP_CONFIG);
  private readonly dvmConfig:             Signal<Configuration['Dvm']>     = computed((): Configuration['Dvm'] => this.config().Dvm);
  private readonly uiconfig:              Signal<UiConfiguration>          = inject(UiService).uiConfig;


  // SERVICES
  public readonly viewer3d:               Viewer3dSdkService               = inject(VIEWER3D);
  private readonly toast:                 ToastService                     = inject(ToastService);
  private readonly viewDetermination:     ViewDeterminationService         = inject(ViewDeterminationService);
  private readonly venueConfigs:          VenueConfigurationsService       = inject(VenueConfigurationsService);
  private readonly destroyRef:            DestroyRef                       = inject(DestroyRef);


  // VIEWERS INIT CONFIGURATION
  private viewer3dInitOptions:             Viewer3dInitOptions             = {};


  // VIEWERS LOAD CONFIGURATION
  public viewer3dLoadOptions:              Viewer3dLoadView3dOptions       = {venue_id: '', view_id:''};


  // STATE
  public  readonly is3d:                   WritableSignal<boolean>         = signal<boolean>(this.dvmConfig().viewer.is3d ?? false);
  public  readonly isFullscreen:           WritableSignal<boolean>         = signal(false);
  private fullscreenEventsBound = false;


  // GETTERS
  /**
   * Devuelve la vista 3D actualmente cargada en el SDK.
   *
   * @returns Identificador de la vista 3D o `null` si el viewer no esta inicializado.
   */
  public  get currentView3d(): string | null {
    return ( this.viewer3d.isInitialized() && this.viewer3d.getViewId() ) ?
      this.viewer3d.getViewId()! :
      null;
  }


  // PUBLIC METHODS

  /**
   * Inicializa el viewer 3D, configura plugins y flags, y carga la vista inicial si procede.
   */
  public initViewer3d(): void {

    const config: Configuration['Dvm']['viewer'] = this.venueConfigs.viewerConfig();

    this.setViewer3dLoadOptions(config);

    // Set 3D plugins
    const plugins = this.dvmConfig().misc.plugins;
    this.viewer3dInitOptions.plugins = plugins.viewer3dPlugins?.length ? plugins.viewer3dPlugins : this.setDefault3dPlugins();

    // Vista inicial solo en la principal: `viewId` se hereda por spread pero no existe en otro
    // venue, y `load3dView` reseteria el viewer en su `catchError`.
    const loadInitialView: boolean = config.is3d && !!config.viewId && !this.venueConfigs.isOverridden();

    if(!this.viewer3d.isInitialized()) {

      this.viewer3d.initialize(this.viewer3dInitOptions).subscribe({
        next: (): void => {

          this.bindFullscreenEvents();

          this.setViewer3dFlags();

          if (loadInitialView) this.load3dView(config.viewId).subscribe();

        },
        error: (error: unknown): void => console.error('Error initializing 3D viewer', error)
      });

    } else {

      this.bindFullscreenEvents();

      if (loadInitialView) this.load3dView(config.viewId).subscribe();

    }

  }

  /**
   * Carga una vista 3D con el `viewVersion` configurado y actualiza el estado de vista.
   *
   * @param viewId Identificador de la vista 3D DVM.
   * @returns Observable emitido por el SDK 3D.
   */
  public load3dView(viewId: string): Observable<Viewer3dLoadObject | Error> {

    const options: Viewer3dLoadView3dOptions = this.getViewer3dLoadOptions(viewId);

    this.toggle3dView(true);

    return from(this.viewer3d.loadView3d(options)).pipe(
      tap((): void => {
        this.viewDetermination.setCurrentView(this.is3d());
      }),
      catchError((): Observable<Error> => {

        const errorMessge: string = 'Error loading 3D view';

        // Si hay error al cargar pano, forzamos 3d view a false y reseteamos view signal
        this.toggle3dView(false);
        this.viewDetermination.setCurrentView(this.is3d());

        // Avisamos al usuario
        this.toast.show(errorMessge, 'error', 2000);
        throw new Error(errorMessge);

      })
    );

  }

  /**
   * Comprueba si existe una vista 3D cuando el SDK esta inicializado.
   *
   * @param viewId Identificador de la vista 3D DVM.
   * @returns `true` si el SDK confirma que existe; los errores de red o permisos se propagan.
   */
  public has3dView(viewId: string): Observable<boolean> {

    const viewerReady$: Observable<Viewer3dSdkService> = this.viewer3d.isInitialized()
      ? of(this.viewer3d)
      : this.viewer3d.waitInitialize().pipe(first());

    return viewerReady$.pipe(
      switchMap((): Observable<boolean> => from(this.viewer3d.has3dView(this.getViewer3dLoadOptions(viewId)))),
      catchError((error: unknown): never => {
        this.toast.show('Error checking 3D view', 'error', 2000);
        throw error;
      })
    );

  }

  /**
   * Cierra el viewer 3D, resetea el SDK y recalcula la vista activa.
   */
  public close3dView(): void {

    this.toggle3dView(false);
    this.isFullscreen.set(false);

    this.viewer3d.reset();

    this.viewDetermination.setCurrentView(this.is3d());

  }


  // PRIVATE METHODS

  /**
   * Construye las opciones compartidas por comprobacion y carga de pano.
   */
  private getViewer3dLoadOptions(viewId: string): Viewer3dLoadView3dOptions {

    const options: Viewer3dLoadView3dOptions = Object.assign({}, this.viewer3dLoadOptions, {view_id: viewId});
    const viewVersion: string = this.uiconfig().View3dDisplay.viewVersion;

    if (viewVersion) options.set_id = viewVersion;

    return options;

  }

  /**
   * Actualiza las opciones de carga 3D con el venue configurado.
   *
   * @param config Configuracion del viewer DVM.
   */
  private setViewer3dLoadOptions(config: Configuration['Dvm']['viewer']): void{

    this.viewer3dLoadOptions.venue_id = config.venueId;

  }

  /**
   * Aplica flags al viewer 3D o restaura los flags por defecto si no se reciben valores.
   *
   * @param flags Flags del SDK 3D que deben sobrescribirse.
   */
  private setViewer3dFlags(flags?: Viewer3dFlagsAPI): void {

    // Default flags
    if(!flags) return this.setDefault3dFlags();

    // Custom flags
    Object.entries(flags).forEach(([key, value]) => {

      if(Object.prototype.hasOwnProperty.call(this.viewer3d.flags, key)){

        this.viewer3d.flags[key as keyof Viewer3dFlagsAPI] = value;

      }

    });

  }

  /**
   * Define los flags base del viewer 3D y del plugin de related previews.
   */
  private setDefault3dFlags(): void {

    this.viewer3d.flags.fixed_aspect_ratio     = false;
    this.viewer3d.flags.show_fullscreen_button = false;

    // RELATED PLUGIN FLAGS
    if(this.viewer3d.related_previews){
      this.viewer3d.related_previews.flags.enable_native_interface = false;
      this.viewer3d.related_previews.flags.automatic_track         = true;
      this.viewer3d.related_previews.flags.enabled                 = true;
    }

  }

  /**
   * Devuelve la lista de plugins base usados por el viewer 3D.
   *
   * @returns Claves de plugins 3D activadas por defecto.
   */
  private setDefault3dPlugins(): (keyof Viewer3dPlugins)[] {

    const plugins: (keyof Viewer3dPlugins)[] = [];

    plugins.push(
      // ...
    );

    return plugins;

  }

  /**
   * Actualiza el signal que indica si el pano 3D esta visible.
   *
   * @param show Nuevo estado visible del viewer 3D.
   */
  private toggle3dView(show: boolean): void {

    if(show !== this.is3d()){

      this.is3d.update(():boolean => show);

    }

  }

  private bindFullscreenEvents(): void {
    if (this.fullscreenEventsBound) return;

    this.fullscreenEventsBound = true;
    this.viewer3d.getObservable('fullscreen_enabled')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.isFullscreen.set(true));
    this.viewer3d.getObservable('fullscreen_disabled')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.isFullscreen.set(false));
  }

}
